import React, { useState } from "react";
import { CheckCircle, Circle, X } from "@phosphor-icons/react";
import { C } from "./shared";

/* Guided first-model checklist — steps tick off automatically as sections
   fill in. Dismissable per model (localStorage); auto-hides when complete. */
export default function GuidedChecklist({ model, out, setTab }) {
  const key = `fm-guide-dismissed-${model.fm_id}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(key) === "1");

  const preRevenue = model.profile?.revenue_status === "pre_revenue";
  const rev = model.assumptions?.revenue || {};
  const costs = model.assumptions?.costs || {};
  const capital = model.assumptions?.capital || {};

  const hasRevenue = !!(out?.dashboard?.f1_revenue || out?.dashboard?.f5_revenue);
  const hasHeadcount = (costs.headcount || []).length > 0;
  const hasHistory = preRevenue
    ? (model.milestones || []).length > 0
    : Object.values(model.historicals || {}).some((h) => h && h.revenue != null);
  const hasCapital = (capital.opening_cash || 0) > 0
    || (capital.equity_raises || []).length > 0 || (capital.debt_facilities || []).length > 0;
  const crsScore = out?.crs?.score ?? 0;
  const crsGood = crsScore >= 80;
  const exported = !!(model.export || {}).paid_at;

  const steps = [
    { id: "revenue", label: "Enter your volume & price", sub: "Your revenue forecast lights up from these two numbers.", done: hasRevenue, tab: "assumptions" },
    { id: "team", label: "Add your headcount plan", sub: "Who you'll hire, when — the biggest cost line for most businesses.", done: hasHeadcount, tab: "assumptions" },
    preRevenue
      ? { id: "history", label: "Map your milestones to first revenue", sub: "Funders want the road to revenue — and whether cash lasts until then.", done: hasHistory, tab: "milestones" }
      : { id: "history", label: "Enter last year's actuals", sub: "Funders read history before forecasts.", done: hasHistory, tab: "historicals" },
    { id: "capital", label: "Set opening cash & funding", sub: "Opening cash, planned raises or debt — this drives your runway.", done: hasCapital, tab: "assumptions" },
    { id: "crs", label: `Clear the CRS checks (${crsScore}/100)`, sub: "The same integrity checks a Clinq analyst runs before a funder sees it.", done: crsGood, tab: "crs" },
    { id: "export", label: "Export your funder-ready model", sub: "Branded Excel + one-pager PDF — use the Export button top-right.", done: exported },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  if (dismissed || allDone) return null;

  const dismiss = () => { localStorage.setItem(key, "1"); setDismissed(true); };

  return (
    <div className="rounded-xl p-5" data-testid="fm-guide"
      style={{ backgroundColor: C.panel, border: "1px solid rgba(245,158,11,0.35)" }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-white font-extrabold text-sm">Build your first funder-ready model</div>
          <div className="text-[11px] text-white/45 mt-0.5">{doneCount} of {steps.length} done — each step ticks off automatically as you fill it in.</div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-28 h-1.5 rounded-full bg-white/10 overflow-hidden mt-1.5">
            <div className="h-full rounded-full transition-all" style={{ width: `${(doneCount / steps.length) * 100}%`, backgroundColor: C.orange }} />
          </div>
          <button onClick={dismiss} data-testid="fm-guide-dismiss" aria-label="Hide checklist"
            className="text-white/40 hover:text-white p-0.5"><X size={15} /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {steps.map((s) => (
          <button key={s.id} disabled={!s.tab || s.done} data-testid={`fm-guide-step-${s.id}`}
            onClick={() => s.tab && setTab(s.tab)}
            className="text-left rounded-lg px-3 py-2.5 flex items-start gap-2.5 transition-colors enabled:hover:bg-white/5 disabled:cursor-default"
            style={{ border: `1px solid ${s.done ? "rgba(44,197,168,0.25)" : "rgba(255,255,255,0.08)"}`, opacity: s.done ? 0.65 : 1 }}>
            {s.done
              ? <CheckCircle size={18} weight="fill" style={{ color: C.green, flexShrink: 0, marginTop: 1 }} />
              : <Circle size={18} style={{ color: C.orange, flexShrink: 0, marginTop: 1 }} />}
            <div>
              <div className={`text-xs font-bold ${s.done ? "text-white/50 line-through" : "text-white"}`}>{s.label}</div>
              <div className="text-[10px] text-white/35 mt-0.5">{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
